<script lang="ts" setup>
import { IrveFormProfileRow } from "@types"
import { ref, computed, watch } from "vue"
import { useI18n } from "vue-i18n"

// Components

// ==================================================================//
//                         Lang
// ==================================================================//
const { t } = useI18n({
    messages: {
        fr: {
            button: {
                label: () => (formEmpty.value ? "Ajouter" : "Modifier"),
                icon: () => (formEmpty.value ? "pi pi-plus" : "pi pi-pencil"),
            },
            all_day: "Toute la journée",
            group_text: "Regrouper les jours similaires",
            info_message:
                "Renseignez les plages horaires d'utilisation des bornes pour une semaine typique.",
        },
    },
})

// ==================================================================//
// Gestion de la vue du profil d'utilisation
// ==================================================================//
type PeriodFoisonnement = {
    period: { start: number; end: number }
    foisonnement: number | undefined
}
/**
 * Indique si le formulaire est vide.
 */
const formEmpty = ref<boolean>(true)

/**
 * État du bouton toggle pour activer/désactiver le regroupement des jours.
 */
const checked = ref<boolean>(true)
/**
 * Récupère les données du formulaire en les regroupant par jour.
 */
const getProfileGroupedByDay = computed(() => {
    const views =
        modelUtilisationProfile.value?.reduce(
            (acc, row) => {
                row.days.forEach((day) => {
                    acc[day] = acc[day] || []
                    row.periods.forEach((period) => {
                        acc[day].push({
                            period,
                            foisonnement: row.foisonnement,
                        })
                    })
                })
                return acc
            },
            {} as Record<string, PeriodFoisonnement[]>
        ) || {}

    return Object.entries(views).map(([day, periods]) => ({
        days: [day],
        periods: periods.sort((a, b) => a.period.start - b.period.start),
    }))
})

/**
 * Récupère les données du formulaire en les regroupants par jour et par période similaire.
 */
const getProfileRowGroupByPeriod = computed(() => {
    const groupedViews =
        modelUtilisationProfile.value?.reduce(
            (acc, row) => {
                const key = JSON.stringify({
                    periods: row.periods,
                    foisonnement: row.foisonnement,
                })

                acc[key] = acc[key] || { days: [], periods: [] }
                acc[key].days.push(...row.days)
                acc[key].periods = row.periods.map((period) => ({
                    period,
                    foisonnement: row.foisonnement,
                }))

                return acc
            },
            {} as Record<
                string,
                { days: string[]; periods: PeriodFoisonnement[] }
            >
        ) || {}

    const usedDays = new Set(
        Object.values(groupedViews).flatMap((group) => group.days)
    )
    const remainingDays = [
        "lundi",
        "mardi",
        "mercredi",
        "jeudi",
        "vendredi",
        "samedi",
        "dimanche",
    ].filter((day) => !usedDays.has(day.toLocaleLowerCase()))

    if (remainingDays.length) {
        groupedViews["undefined"] = { days: remainingDays, periods: [] }
    }

    return Object.values(groupedViews).map((group) => ({
        days: group.days,
        periods: group.periods.sort((a, b) => a.period.start - b.period.start),
    }))
})

/**
 * Formate un nombre représentant une heure en une chaîne au format 'HH:00'.
 * @param hour - L'heure à formater, entre 0 et 23 inclus.
 * @returns L'heure formatée au format 'HH:00'.
 */
const formatToHour = (hour: number) => hour.toString().padStart(2, "0") + ":00"

// ==================================================================//
// Gestion du formulaire du profil d'utilisation
// ==================================================================//
/**
 * Détermine si le modal est visible ou non.
 */
const visible = ref<boolean>(false)
/**
 * Modèle représentant les données du formulaire d'utilisation.
 */
const modelUtilisationProfile =
    defineModel<IrveFormProfileRow[]>("utilisationProfile")

const emit = defineEmits(["update:utilisationProfile"])

function onUpdateUtilisationProfile(value: IrveFormProfileRow[]) {
    modelUtilisationProfile.value = value
    // Envoie les données mises à jour au parent
    emit("update:utilisationProfile", value)
}

/**
 * Surveille les changements dans la longueur du formulaire pour déterminer s'il est vide ou non.
 */
watch(
    () => modelUtilisationProfile.value?.length,
    (length) => {
        formEmpty.value = !length
    }
)
</script>

<template>
    <div class="panel-utilisation-profile flex flex-col justify-center gap-4">
        <BaseMessage class="panel-utilisation-profile__errors" severity="info">
            {{ t("info_message") }}</BaseMessage
        >
        <!-- Toggle pour regrouper les jours similaires -->
        <div v-if="!formEmpty" class="flex gap-2">
            <BaseToggleSwitch v-model="checked" />
            <p>{{ t("group_text") }}</p>
        </div>

        <!-- Affichage des données du formulaire -->
        <div v-if="!formEmpty">
            <div class="flex flex-col gap-2">
                <!-- Liste des périodes regroupées ou non -->
                <div
                    v-for="(group, index) in checked
                        ? getProfileRowGroupByPeriod
                        : getProfileGroupedByDay"
                    :key="index"
                    class="group flex rounded-md p-2 hover:bg-cyan-50"
                >
                    <!-- Jours -->
                    <h3
                        class="w-80 shrink-0 border-r-2 border-r-slate-200 capitalize group-hover:border-r-cyan-200"
                    >
                        {{ group.days.join(", ") }}
                    </h3>
                    <!-- Périodes et foisonnements -->
                    <div
                        class="group/pf mx-2 flex-1 rounded-md hover:bg-cyan-100"
                        v-if="group.periods?.length"
                    >
                        <div
                            v-for="pf in group.periods"
                            :key="pf.period.start + pf.period.end"
                            class="m-2 flex flex-col border-l-2 border-l-slate-200 p-2 group-hover/pf:border-l-cyan-200"
                        >
                            <p
                                v-if="
                                    pf.period.start !== 0 && pf.period.end !== 0
                                "
                            >
                                {{ formatToHour(pf.period.start) }} -
                                {{ formatToHour(pf.period.end) }}
                            </p>
                            <p v-else>{{ t("all_day") }}</p>
                            <p>
                                {{
                                    pf.foisonnement
                                        ? Number(
                                              (pf.foisonnement * 100).toFixed(2)
                                          )
                                        : 0
                                }}%
                            </p>
                        </div>
                    </div>
                    <!-- Indication "Fermé" si aucune période -->
                    <div v-else>
                        <p class="p-2">Fermé</p>
                    </div>
                </div>
            </div>
        </div>
        <slot name="error"></slot>
        <!-- Bouton pour ouvrir le modal -->
        <div class="flex justify-center">
            <BaseButton
                :label="t('button.label')"
                severity="secondary"
                :icon="t('button.icon')"
                @click="visible = true"
            />
        </div>

        <!-- Modal du formulaire -->
        <UtilisationProfileModal
            :visible="visible"
            :utilisation-profile="modelUtilisationProfile"
            @close="visible = false"
            @update:utilisation-profile="onUpdateUtilisationProfile"
        />
    </div>
</template>
